const BASE = "https://ginbecc-backend.onrender.com/api/v1";

export const tokenStorage = {
  get:        ()  => localStorage.getItem("token"),
  set:        (t) => localStorage.setItem("token", t),
  getRefresh: ()  => localStorage.getItem("refreshToken"),
  setRefresh: (t) => localStorage.setItem("refreshToken", t),
  clear:      ()  => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
  },
};

function forceLogout() {
  tokenStorage.clear();
  window.dispatchEvent(new Event("auth:logout"));
}

export function isTokenExpired(token) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (!payload.exp) return false;
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
}

async function req(method, path, body = null, isForm = false) {
  let token = tokenStorage.get();

  if (token && isTokenExpired(token) && tokenStorage.getRefresh()) {
    const refreshed = await tryRefreshToken();
    token = refreshed ? tokenStorage.get() : null;
    if (!refreshed) { forceLogout(); return; }
  }

  const headers = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (!isForm) headers["Content-Type"] = "application/json";

  const options = { method, headers };
  if (body) options.body = isForm ? body : JSON.stringify(body);

  const res = await fetch(`${BASE}${path}`, options);

  if (res.status === 401) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      headers["Authorization"] = `Bearer ${tokenStorage.get()}`;
      const retry = await fetch(`${BASE}${path}`, { ...options, headers });
      if (retry.status === 401) { forceLogout(); return; }
      if (!retry.ok) throw new Error(`Error ${retry.status}`);
      if (retry.status === 204) return null;
      return retry.json();
    }
    forceLogout();
    return;
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const errMsg = new Error(err.message || `Error ${res.status}`);
    errMsg.body   = err;
    errMsg.status = res.status;
    throw errMsg;
  }

  if (res.status === 204) return null;
  return res.json();
}

let refreshPromise = null;

export function tryRefreshToken() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const rt = tokenStorage.getRefresh();
      if (!rt) return false;
      const res = await fetch(`${BASE}/auth/refresh-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: rt }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      const accessToken  = data.data?.accessToken;
      const refreshToken = data.data?.refreshToken;
      if (!accessToken) return false;
      tokenStorage.set(accessToken);
      if (refreshToken) tokenStorage.setRefresh(refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

const get    = (path)        => req("GET",    path);
const post   = (path, body)  => req("POST",   path, body);
const put    = (path, body)  => req("PUT",    path, body);
const patch  = (path, body)  => req("PATCH",  path, body);
const del    = (path)        => req("DELETE", path);
const upload = (path, form)  => req("POST",   path, form, true);

function qs(params = {}) {
  const filtered = Object.entries(params).filter(
    ([, v]) => v !== null && v !== undefined && v !== ""
  );
  return filtered.length ? "?" + new URLSearchParams(filtered).toString() : "";
}

export const authAPI = {
  login:          (data)           => post("/auth/login", data),
  logout:         ()               => fetch(`${BASE}/auth/logout`, { method: "POST", headers: { "Authorization": `Bearer ${tokenStorage.get()}` } }),
  refreshToken:   (token)          => post("/auth/refresh-token", { refreshToken: token }),
  changePassword: (userId, data)   => post(`/auth/change-password?userId=${userId}`, data),
  resetPassword:  (userId)         => post(`/auth/reset-password/${userId}`),
};

export const dashboardAPI = {
  getSummary: (userId) => get(`/dashboard/summary${qs({ userId })}`),
};

export const officerAPI = {
  getAll:           (page=0, size=20, dept, status) =>
                      get(`/officers${qs({ page, size, departmentId: dept, status })}`),
  getById:          (id)         => get(`/officers/${id}`),
  create:           (data)       => post("/officers", data),
  update:           (id, data)   => put(`/officers/${id}`, data),
  updateStatus:     (id, data)   => patch(`/officers/${id}/status`, data),
  delete:           (id)         => del(`/officers/${id}`),
  getNearRetirement:()           => get("/officers/near-retirement"),
  uploadImage:      (id, file)   => { const f = new FormData(); f.append("file", file); return upload(`/officers/${id}/profile-image`, f); },
  getProfileImage:  (id)         => get(`/officers/${id}/profile-image`),
};

export const contractOfficerAPI = {
  getAll:       (page=0, size=20, status, dept, expiring_within) =>
                  get(`/contract-officers${qs({ page, size, status, dept, expiring_within })}`),
  getById:      (id)       => get(`/contract-officers/${id}`),
  create:       (data)     => post("/contract-officers", data),
  update:       (id, data) => put(`/contract-officers/${id}`, data),
  updateStatus: (id, data) => patch(`/contract-officers/${id}/status`, data),
  delete:       (id)       => del(`/contract-officers/${id}`),
  uploadImage:  (id, file) => { const f = new FormData(); f.append("file", file); return upload(`/contract-officers/${id}/profile-image`, f); },
  getProfileImage: (id)    => get(`/contract-officers/${id}/profile-image`),
};

export const documentAPI = {
  getAll:    (page=0, size=20, officer_id, type, status, expiring_within) =>
               get(`/documents${qs({ page, size, officer_id, type, status, expiring_within })}`),
  getById:   (id)       => get(`/documents/${id}`),
  create:    (data)     => post("/documents", data),
  update:    (id, data) => put(`/documents/${id}`, data),
  delete:    (id)       => del(`/documents/${id}`),
  uploadAttachment: (id, file) => { const f = new FormData(); f.append("file", file); return upload(`/documents/${id}/attachment`, f); },
  getDownloadUrl:   (id)       => get(`/documents/${id}/download`),
};

export const documentTypeAPI = {
  getAll:  ()           => get("/document-types"),
  getById: (id)         => get(`/document-types/${id}`),
  create:  (data)       => post("/document-types", data),
  update:  (id, data)   => put(`/document-types/${id}`, data),
  delete:  (id)         => del(`/document-types/${id}`),
};

export const meetingAPI = {
  getAll:           (page=0, size=20, status, room_id) =>
                      get(`/meetings${qs({ page, size, status, roomId: room_id })}`),
  getById:          (id)                           => get(`/meetings/${id}`),
  getCalendar:      (month, year)                  => get(`/meetings/calendar${qs({ month, year })}`),
  checkAvailability:(roomId, date, startTime, endTime) =>
                      get(`/meetings/rooms/${roomId}/availability${qs({ date, startTime, endTime })}`),
  getRoomSchedule:  (roomId, date)                 => get(`/meetings/rooms/${roomId}/schedule${qs({ date })}`),
  create:           (data)                         => post("/meetings", data),
  update:           (id, data)                     => put(`/meetings/${id}`, data),
  delete:           (id)                           => del(`/meetings/${id}`),
  addAttendeesBulk: (id, attendees)                => post(`/meetings/${id}/attendees/bulk`, attendees),
  updateAttendance: (id, aid, data)                => patch(`/meetings/${id}/attendees/${aid}/attendance`, data),
};

export const attendeeAPI = {
  getAll:   (meetingId)         => get(`/meetings/${meetingId}/attendees`),
  getById:  (meetingId, id)     => get(`/meetings/${meetingId}/attendees/${id}`),
  add:      (meetingId, data)   => post(`/meetings/${meetingId}/attendees`, data),
  remove:   (meetingId, id)     => del(`/meetings/${meetingId}/attendees/${id}`),
};

export const meetingRoomAPI = {
  getAll:      (status)     => get(`/meeting-rooms${qs({ status })}`),
  getById:     (id)         => get(`/meeting-rooms/${id}`),
  create:      (data)       => post("/meeting-rooms", data),
  update:      (id, data)   => put(`/meeting-rooms/${id}`, data),
  delete:      (id)         => del(`/meeting-rooms/${id}`),
  uploadImage:  (id, file)   => { const f = new FormData(); f.append("file", file); return upload(`/meeting-rooms/${id}/image`, f); },
  getImageUrl:  (id)         => get(`/meeting-rooms/${id}`),
  getSchedule:  (id, date)   => get(`/meeting-rooms/${id}/schedule${qs({ date })}`),
};

export const meetingMinuteAPI = {
  getAll:         (page=0, size=20, meeting_id) =>
                    get(`/meeting-minutes${qs({ page, size, meeting_id })}`),
  getById:        (id)        => get(`/meeting-minutes/${id}`),
  create:         (data)      => post("/meeting-minutes", data),
  update:         (id, data)  => put(`/meeting-minutes/${id}`, data),
  uploadFile:     (id, file)  => { const f = new FormData(); f.append("file", file); return upload(`/meeting-minutes/${id}/attachment`, f); },
  getDownloadUrl: (id)        => get(`/meeting-minutes/${id}/download`),
};

export const announcementAPI = {
  getAll:      (page=0, size=20, status, priority) =>
                 get(`/announcements${qs({ page, size, status, priority })}`),
  getById:     (id)               => get(`/announcements/${id}`),
  getReadStatus:(id)              => get(`/announcements/${id}/read-status`),
  create:      (data)             => post("/announcements", data),
  update:      (id, data)         => put(`/announcements/${id}`, data),
  delete:      (id)               => del(`/announcements/${id}`),
  // marks the announcement read for the CURRENT JWT user — the API has no
  // per-recipient variant, so an admin cannot mark it read on someone's behalf
  markAsRead:  (id)               => post(`/announcements/${id}/mark-read`),
  uploadFile:  (id, file)         => { const f = new FormData(); f.append("file", file); return upload(`/announcements/${id}/attachment`, f); },
};

export const departmentAPI = {
  getAll:  (status, keyword) => get(`/departments${qs({ status, keyword })}`),
  getById: (id)              => get(`/departments/${id}`),
  create:  (data)            => post("/departments", data),
  update:  (id, data)        => put(`/departments/${id}`, data),
  delete:  (id)              => del(`/departments/${id}`),
  // managerUserId / isPrimary are query params, not a JSON body
  getManagers:   (id)                            => get(`/departments/${id}/managers`),
  addManager:    (id, managerUserId, isPrimary=false) =>
                   post(`/departments/${id}/managers${qs({ managerUserId, isPrimary })}`),
  removeManager: (id, userId)                    => del(`/departments/${id}/managers/${userId}`),
};

export const positionAPI = {
  getAll:  (keyword) => get(`/positions${qs({ keyword })}`),
  getById: (id)      => get(`/positions/${id}`),
  create:  (data)    => post("/positions", data),
  update:  (id, data)=> put(`/positions/${id}`, data),
  delete:  (id)      => del(`/positions/${id}`),
};

export const officeAPI = {
  getAll:  (status, keyword) => get(`/offices${qs({ status, keyword })}`),
  getById: (id)               => get(`/offices/${id}`),
  create:  (data)             => post("/offices", data),
  update:  (id, data)         => put(`/offices/${id}`, data),
  delete:  (id)               => del(`/offices/${id}`),
};

export const roleAPI = {
  getAll:            ()           => get("/roles"),
  getById:           (id)         => get(`/roles/${id}`),
  create:            (data)       => post("/roles", data),
  update:            (id, data)   => put(`/roles/${id}`, data),
  delete:            (id)         => del(`/roles/${id}`),
  getPermissions:    (id)         => get(`/roles/${id}/permissions`),
  assignPermissions: (id, data)   => post(`/roles/${id}/permissions`, data),
  updateWithPerms: (id, data)    => put(`/roles/${id}`, data),
};

export const permissionAPI = {
  getAll:      ()           => get("/permissions"),
  getById:     (id)         => get(`/permissions/${id}`),
  getByModule: (module)     => get(`/permissions/module/${module}`),
  create:      (data)       => post("/permissions", data),
  update:      (id, data)   => put(`/permissions/${id}`, data),
  delete:      (id)         => del(`/permissions/${id}`),
};

export const userAPI = {
  getAll:       (page=0, size=20, roleId, status, keyword) =>
                  get(`/users${qs({ page, size, roleId, status, keyword })}`),
  getById:      (id)       => get(`/users/${id}`),
  getPermissions:(id)      => get(`/users/${id}/permissions`),
  create:       (data)     => post("/users", data),
  update:       (id, data) => put(`/users/${id}`, data),
  updateStatus: (id, data) => patch(`/users/${id}/status`, data),
  delete:       (id)       => del(`/users/${id}`),
  uploadImage:  (id, file) => { const f = new FormData(); f.append("file", file); return upload(`/users/${id}/profile-image`, f); },
  getProfileImage: (id)    => get(`/users/${id}/profile-image`),
};

export const activityLogAPI = {
  getAll: (page=0, size=30, action, entityType, userId, from, to) =>
    get(`/audit-logs${qs({ page, size, action, entityType, userId, from, to })}`),
  getById: (id) => get(`/audit-logs/${id}`),
};

export const approvalAPI = {
  getAll:   (page=0, size=20, status) =>
              get(`/approvals${qs({ page, size, status })}`),
  getById:  (id)       => get(`/approvals/${id}`),
  request:  (data)     => post("/approvals", data),
  decide:   (id, data) => put(`/approvals/${id}/decide`, data),
};

export const notificationAPI = {
  getMy:        (userId, page=0, size=20, isRead) =>
                  get(`/notifications/my${qs({ userId, page, size, isRead })}`),
  getById:      (id)      => get(`/notifications/${id}`),
  getUnreadCount:(userId) => get(`/notifications/unread-count${qs({ userId })}`),
  create:       (data)    => post("/notifications", data),
  createBulk:   (data)    => post("/notifications/bulk", data),
  markAsRead:   (id)      => put(`/notifications/${id}/read`),
  markAllRead:  (userId)  => put(`/notifications/read-all${qs({ userId })}`),
};

export const attachmentAPI = {
  getAll:          (page=0, size=30, ref_type) =>
                     get(`/attachments${qs({ page, size, reference_type: ref_type })}`),
  upload:          (file, ref_type, ref_id) => {
                     const f = new FormData();
                     f.append("file", file);
                     f.append("ref_type", ref_type);
                     f.append("ref_id", ref_id);
                     return upload("/attachments/upload", f);
                   },
  getByReference:  (ref_id, type="OFFICER") => get(`/attachments/reference/${ref_id}${qs({ type })}`),
  getActive:       (ref_id, type="OFFICER") => get(`/attachments/reference/${ref_id}/active${qs({ type })}`),
  download:        (id)                     => get(`/attachments/${id}/download`),
  getDownloadUrl:  (id)                     => get(`/attachments/${id}/download`),
  setActive:       (id)                     => put(`/attachments/${id}/set-current`),
  delete:          (id)                     => del(`/attachments/${id}`),
  cleanupArchived: (ref_id, type)           => del(`/attachments/reference/${ref_id}/archived${qs({ type })}`),
};

export const auditLogAPI = {
  getAll:  (userId, action, entityType, from, to, page=0, size=20) =>
             get(`/audit-logs${qs({ userId, action, entityType, from, to, page, size })}`),
  getById: (id) => get(`/audit-logs/${id}`),
};

export const reportAPI = {
  exportUsers:           (p = {}) => downloadFile(`/reports/users${qs(p)}`,              "users_report.xlsx"),
  exportOfficers:        (p = {}) => downloadFile(`/reports/officers${qs(p)}`,            "officers_report.xlsx"),
  exportNotifications:   (p = {}) => downloadFile(`/reports/notifications${qs(p)}`,       "notifications_report.xlsx"),
  exportMeetings:        (p = {}) => downloadFile(`/reports/meetings${qs(p)}`,            "meetings_report.xlsx"),
  exportMeetingMinutes:  (p = {}) => downloadFile(`/reports/meeting-minutes${qs(p)}`,     "meeting_minutes_report.xlsx"),
  exportDocuments:       (p = {}) => downloadFile(`/reports/documents${qs(p)}`,           "documents_report.xlsx"),
  exportContractOfficers:(p = {}) => downloadFile(`/reports/contract-officers${qs(p)}`,   "contract_officers_report.xlsx"),
  exportAuditLogs:       (p = {}) => downloadFile(`/reports/audit-logs${qs(p)}`,          "audit_logs_report.xlsx"),
  exportApprovals:       (p = {}) => downloadFile(`/reports/approvals${qs(p)}`,           "approvals_report.xlsx"),
  exportAnnouncements:   (p = {}) => downloadFile(`/reports/announcements${qs(p)}`,       "announcements_report.xlsx"),
  exportAnnouncementRecipients: (id, p = {}) =>
    downloadFile(`/reports/announcements/${id}/recipients${qs(p)}`, `announcement_${id}_recipients_report.xlsx`),

  previewUsers:           (p = {}) => get(`/reports/users/preview${qs(p)}`),
  previewOfficers:        (p = {}) => get(`/reports/officers/preview${qs(p)}`),
  previewNotifications:   (p = {}) => get(`/reports/notifications/preview${qs(p)}`),
  previewMeetings:        (p = {}) => get(`/reports/meetings/preview${qs(p)}`),
  previewMeetingMinutes:  (p = {}) => get(`/reports/meeting-minutes/preview${qs(p)}`),
  previewDocuments:       (p = {}) => get(`/reports/documents/preview${qs(p)}`),
  previewContractOfficers:(p = {}) => get(`/reports/contract-officers/preview${qs(p)}`),
  previewAuditLogs:       (p = {}) => get(`/reports/audit-logs/preview${qs(p)}`),
  previewApprovals:       (p = {}) => get(`/reports/approvals/preview${qs(p)}`),
  previewAnnouncements:   (p = {}) => get(`/reports/announcements/preview${qs(p)}`),
  previewAnnouncementRecipients: (id, p = {}) =>
    get(`/reports/announcements/${id}/recipients/preview${qs(p)}`),
};

async function downloadFile(path, filename) {
  const token = tokenStorage.get();
  const res = await fetch(`${BASE}${path}`, {
    headers: { "Authorization": `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Download failed: ${res.status}`);
  const blob = await res.blob();
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const lookupAPI = {
  getOfficerStatuses:      ()          => get("/lookups/officer-statuses"),
  getDocumentStatuses:     (appliesTo) => get(`/lookups/document-statuses${qs({ appliesTo })}`),
  getMeetingStatuses:      ()          => get("/lookups/meeting-statuses"),
  getAnnouncementStatuses: ()          => get("/lookups/announcement-statuses"),
  getUserStatuses:         ()          => get("/lookups/user-statuses"),
  getAll:                  ()          => get("/lookups/all"),
};
